/**
 * NotificationCenter — bell icon + dropdown panel for the top navbar.
 *
 * Features:
 *  - Bell icon with red unread badge count (capped at 99+)
 *  - Click opens a dropdown panel (not a full page)
 *  - Notifications grouped by time: Today, Yesterday, This Week, Older
 *  - Each notification: icon by type, title, body preview, time ago, click to navigate
 *  - "Mark all read" button
 *  - "Settings" link to /settings/notifications
 *  - SSE for real-time push of new notifications
 *  - 30-second polling fallback for unread count
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, getToken } from '@/lib/api'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Notification {
  id: string
  /** Streamer event types: follow, subscription, raid, donation, team_invite,
   *  goal_complete, automation_fired, stream_report_ready, system */
  type: string
  title: string
  body: string
  /** Type-specific payload: follower_id, amount, raider_display_name, etc. */
  data: Record<string, unknown>
  is_read: boolean
  read_at: string | null
  /** Deep link into the app — navigate here on click when present. */
  action_url: string | null
  created_at: string
}

interface UnreadCountResponse {
  count: number
}

interface NotificationsResponse {
  notifications: Notification[]
}

// ── Time grouping ─────────────────────────────────────────────────────────────

type TimeGroup = 'today' | 'yesterday' | 'week' | 'older'

const GROUP_LABELS: Record<TimeGroup, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  week: 'This Week',
  older: 'Older',
}

const GROUP_ORDER: TimeGroup[] = ['today', 'yesterday', 'week', 'older']

function timeGroup(iso: string): TimeGroup {
  const now = new Date()
  const date = new Date(iso)
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterdayStart = new Date(todayStart.getTime() - 86_400_000)
  const weekStart = new Date(todayStart.getTime() - 6 * 86_400_000)
  if (date >= todayStart) return 'today'
  if (date >= yesterdayStart) return 'yesterday'
  if (date >= weekStart) return 'week'
  return 'older'
}

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime()
  const s = Math.floor(diffMs / 1000)
  if (s < 60) return `${s}s ago`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

// ── Type metadata ─────────────────────────────────────────────────────────────

const TYPE_META: Record<string, { icon: string; color: string }> = {
  follow:               { icon: '👥', color: '#38bdf8' },
  subscription:         { icon: '⭐', color: '#a78bfa' },
  raid:                 { icon: '⚔️', color: '#f59e0b' },
  donation:             { icon: '💰', color: '#4ade80' },
  team_invite:          { icon: '🤝', color: '#818cf8' },
  goal_complete:        { icon: '🎯', color: '#34d399' },
  automation_fired:     { icon: '⚡', color: '#fb923c' },
  stream_report_ready:  { icon: '📊', color: '#60a5fa' },
  system:               { icon: '⚙️', color: '#94a3b8' },
}

const DEFAULT_META = { icon: '🔔', color: 'var(--accent)' }

function typeMeta(type: string) {
  return TYPE_META[type] ?? DEFAULT_META
}

// ── SVG icons ─────────────────────────────────────────────────────────────────

function BellSvg() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  )
}

function GearSvg() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  )
}

// ── Notification item ─────────────────────────────────────────────────────────

interface NotifItemProps {
  notification: Notification
  onRead: (id: string, actionUrl: string | null) => void
}

function NotifItem({ notification: n, onRead }: NotifItemProps) {
  const meta = typeMeta(n.type)
  return (
    <li
      role="button"
      tabIndex={0}
      aria-label={`${n.is_read ? '' : 'Unread: '}${n.title}`}
      className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-white/5 transition-colors"
      style={{
        borderLeft: n.is_read ? '3px solid transparent' : `3px solid ${meta.color}`,
      }}
      onClick={() => onRead(n.id, n.action_url)}
      onKeyDown={(e) => e.key === 'Enter' && onRead(n.id, n.action_url)}
    >
      {/* Type icon badge */}
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center text-sm shrink-0 mt-0.5"
        style={{ backgroundColor: `${meta.color}22`, color: meta.color }}
        aria-hidden="true"
      >
        {meta.icon}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p
          className="text-sm font-medium leading-tight truncate"
          style={{ color: n.is_read ? 'var(--text-secondary)' : '#ffffff' }}
        >
          {n.title}
        </p>
        {n.body && (
          <p
            className="text-xs mt-0.5 leading-snug line-clamp-2"
            style={{ color: 'var(--text-muted)' }}
          >
            {n.body}
          </p>
        )}
        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)', opacity: 0.6 }}>
          {relativeTime(n.created_at)}
        </p>
      </div>

      {/* Unread indicator dot */}
      {!n.is_read && (
        <div
          className="w-2 h-2 rounded-full shrink-0 mt-2"
          style={{ backgroundColor: meta.color }}
          aria-hidden="true"
        />
      )}
    </li>
  )
}

// ── Group header ──────────────────────────────────────────────────────────────

function GroupHeader({ label }: { label: string }) {
  return (
    <li
      className="px-4 py-1.5 sticky top-0 z-10"
      style={{ backgroundColor: 'var(--bg-card)' }}
      role="presentation"
    >
      <span
        className="text-[10px] font-semibold uppercase tracking-widest"
        style={{ color: 'var(--text-muted)' }}
      >
        {label}
      </span>
    </li>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function NotificationCenter() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const esRef = useRef<EventSource | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // ── Unread badge count poll (30s) ─────────────────────────────────────────
  const fetchCount = useCallback(() => {
    api
      .get<UnreadCountResponse>('/notifications/unread-count')
      .then((r) => setUnreadCount(r.count ?? 0))
      .catch(() => null)
  }, [])

  useEffect(() => {
    fetchCount()
    pollRef.current = setInterval(fetchCount, 30_000)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [fetchCount])

  // ── SSE: real-time push of new notifications ──────────────────────────────
  useEffect(() => {
    const token = getToken()
    if (!token) return

    const url = `/api/notifications/stream?token=${encodeURIComponent(token)}`
    const es = new EventSource(url)
    esRef.current = es

    es.addEventListener('notification', (evt: MessageEvent) => {
      try {
        const incoming = JSON.parse(evt.data as string) as Notification
        if (!incoming.is_read) {
          setUnreadCount((c) => c + 1)
        }
        setNotifications((prev) => {
          if (prev.some((n) => n.id === incoming.id)) return prev
          return [incoming, ...prev].slice(0, 50)
        })
      } catch {
        // ignore malformed push events
      }
    })

    return () => {
      es.close()
      esRef.current = null
    }
  }, [])

  // ── Load notifications when panel opens ───────────────────────────────────
  useEffect(() => {
    if (!open) return
    setLoading(true)
    api
      .get<NotificationsResponse>('/notifications?limit=20')
      .then((r) => setNotifications(r.notifications ?? []))
      .catch(() => null)
      .finally(() => setLoading(false))
  }, [open])

  // ── Close on outside click ────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return
    function handle(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [open])

  // ── Close on Escape ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open])

  // ── Mark single notification as read + navigate if action_url ────────────
  const handleRead = useCallback(
    (id: string, actionUrl: string | null) => {
      const n = notifications.find((x) => x.id === id)
      if (n && !n.is_read) {
        setNotifications((prev) =>
          prev.map((x) => (x.id === id ? { ...x, is_read: true } : x))
        )
        setUnreadCount((c) => Math.max(0, c - 1))
        api.post(`/notifications/${id}/read`).catch(() => null)
      }
      if (actionUrl) {
        setOpen(false)
        navigate(actionUrl)
      }
    },
    [navigate, notifications]
  )

  // ── Mark all as read ──────────────────────────────────────────────────────
  const handleMarkAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })))
    setUnreadCount(0)
    api.post('/notifications/read-all').catch(() => null)
  }, [])

  // ── Group notifications by time ───────────────────────────────────────────
  const grouped = notifications.reduce<Record<TimeGroup, Notification[]>>(
    (acc, n) => {
      const g = timeGroup(n.created_at)
      acc[g].push(n)
      return acc
    },
    { today: [], yesterday: [], week: [], older: [] }
  )

  const badgeCount = Math.min(unreadCount, 99)
  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount)

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        type="button"
        aria-label={
          unreadCount > 0
            ? `${badgeLabel} unread notifications, open panel`
            : 'Notifications'
        }
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="relative flex items-center justify-center w-9 h-9 rounded-lg transition-colors hover:bg-white/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500"
        style={{ color: open ? 'var(--text-primary)' : 'var(--text-muted)' }}
      >
        <BellSvg />

        {/* Red unread badge */}
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full text-white font-bold leading-none"
            style={{
              backgroundColor: '#ef4444',
              fontSize: badgeCount > 9 ? 9 : 10,
            }}
            aria-hidden="true"
          >
            {badgeLabel}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 rounded-xl shadow-2xl z-50 overflow-hidden"
          style={{
            backgroundColor: 'var(--bg-card)',
            border: '1px solid var(--border-primary)',
          }}
          role="dialog"
          aria-label="Notifications panel"
        >
          {/* Panel header */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ borderBottom: '1px solid var(--border-primary)' }}
          >
            <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              Notifications
              {unreadCount > 0 && (
                <span
                  className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: '#ef4444', color: '#fff' }}
                >
                  {badgeLabel}
                </span>
              )}
            </span>

            <div className="flex items-center gap-3">
              {/* Mark all read */}
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllRead}
                  className="text-xs transition-opacity hover:opacity-70"
                  style={{ color: 'var(--accent-light)' }}
                >
                  Mark all read
                </button>
              )}

              {/* Settings link */}
              <Link
                to="/settings/notifications"
                className="flex items-center gap-1 text-xs transition-opacity hover:opacity-70"
                style={{ color: 'var(--text-muted)' }}
                onClick={() => setOpen(false)}
                aria-label="Notification settings"
              >
                <GearSvg />
                Settings
              </Link>
            </div>
          </div>

          {/* Notification list grouped by time */}
          <ul
            className="overflow-y-auto"
            style={{ maxHeight: 400 }}
            role="list"
            aria-label="Notifications"
          >
            {loading && (
              <li
                className="px-4 py-8 text-center text-sm"
                style={{ color: 'var(--text-muted)' }}
              >
                Loading…
              </li>
            )}

            {!loading && notifications.length === 0 && (
              <li
                className="px-4 py-8 text-center text-sm"
                style={{ color: 'var(--text-muted)' }}
              >
                No notifications yet.
              </li>
            )}

            {!loading &&
              GROUP_ORDER.map((group) => {
                const items = grouped[group]
                if (items.length === 0) return null
                return (
                  <>
                    <GroupHeader key={`header-${group}`} label={GROUP_LABELS[group]} />
                    {items.map((n) => (
                      <NotifItem key={n.id} notification={n} onRead={handleRead} />
                    ))}
                  </>
                )
              })}
          </ul>

          {/* Footer — see all / settings */}
          <div
            className="flex items-center justify-between px-4 py-2.5"
            style={{ borderTop: '1px solid var(--border-primary)' }}
          >
            <Link
              to="/notifications"
              className="text-xs transition-opacity hover:opacity-70"
              style={{ color: 'var(--text-muted)' }}
              onClick={() => setOpen(false)}
            >
              See all notifications →
            </Link>
            <Link
              to="/settings/notifications"
              className="text-xs transition-opacity hover:opacity-70"
              style={{ color: 'var(--text-muted)' }}
              onClick={() => setOpen(false)}
            >
              Preferences
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
